'use client';

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { ChevronLeft, ChevronRight, ArrowUpRight, Play } from 'lucide-react';
import ParticleSphere from './ParticleSphere';
import { loadPublishedWorks, localWorkRecords, type WorkRecord } from '@/app/lib/works';
import { SECTION_HEADING_CLASS, SECTION_HEADING_STYLE } from './sectionHeading';

/**
 * Selected Work.
 *
 * The structure is taken from the reference: a two-part heading, then one wide
 * band divided into grid cells by hairlines — a generative sphere anchoring the
 * left, the desktop screenshot and a strip of supporting shots in the middle, a
 * portrait shot beside it, and the description in the right-hand column — with
 * a control bar underneath carrying prev/next and the project name.
 *
 * What is deliberately not taken: the reference advances that band by pinning
 * the section and converting vertical scroll into horizontal movement. It looks
 * superb and it hides content from Ctrl+F, from anyone who scrolls fast, and
 * from a screen reader's reading order. A portfolio can afford that trade; a
 * site whose job is to get an SMB owner to book a call cannot. The same band
 * advances here on explicit controls and arrow keys, so every project is
 * reachable and nothing is hostage to a scroll gesture.
 *
 * The mono readout in the bottom-left corner echoes the reference's habit of
 * printing live CSS values as ornament. Ours prints the carousel's real index
 * and offset, so it is describing something true rather than decorating.
 */
export default function SelectedWorkSection() {
  const [index, setIndex] = useState(0);
  const [offset, setOffset] = useState(0);
  // Seeded, not empty: the section has to exist in the server-rendered HTML
  // and paint before the database round trip, or it is missing from the page
  // for crawlers and for the first second of every visit.
  const [works, setWorks] = useState<WorkRecord[]>(localWorkRecords);
  const frameRef = useRef<HTMLDivElement>(null);

  // Published projects come from the `works` table, managed in the admin
  // dashboard's Works panel. An unconfigured or failing backend — or a table
  // with nothing published yet — falls back to workData.ts inside
  // loadPublishedWorks, so a database outage cannot blank the section.
  useEffect(() => {
    let cancelled = false;
    void loadPublishedWorks().then((res) => {
      if (cancelled) return;
      setWorks(res.works);
      setIndex((i) => (i < res.works.length ? i : 0));
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const total = works.length;
  const item = works[index];

  /* Everything showable for this project, in one list: the landscape video if
     there is one (with the desktop still as its poster), otherwise the desktop
     still, then the supporting shots. The strip below the main frame is this
     same list, which is what lets a visitor click back to the opening shot
     after browsing away from it. */
  const media = useMemo(() => {
    if (!item) return [] as { kind: 'video' | 'image'; src: string; poster?: string }[];
    const list: { kind: 'video' | 'image'; src: string; poster?: string }[] = [];
    if (item.desktopVideo)
      list.push({ kind: 'video', src: item.desktopVideo, poster: item.desktopImages[0] });
    item.desktopImages.forEach((src) => list.push({ kind: 'image', src }));
    return list;
  }, [item]);

  const [mediaIndex, setMediaIndex] = useState(0);
  const [mobileIndex, setMobileIndex] = useState(0);
  // Changing project has to reset the frame, or project B opens on whichever
  // supporting shot happened to be selected for project A.
  useEffect(() => {
    setMediaIndex(0);
    setMobileIndex(0);
  }, [index]);
  const active = media[Math.min(mediaIndex, Math.max(0, media.length - 1))];

  const go = useCallback(
    (dir: number) => {
      if (total < 2) return;
      setIndex((i) => (i + dir + total) % total);
      setOffset(dir * 24);
      window.setTimeout(() => setOffset(0), 40);
    },
    [total]
  );

  // Arrow keys work when the band has focus, which is what makes the carousel
  // operable without a pointer.
  useEffect(() => {
    const el = frameRef.current;
    if (!el || total < 2) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        go(-1);
      }
      if (e.key === 'ArrowRight') {
        e.preventDefault();
        go(1);
      }
    };
    el.addEventListener('keydown', onKey);
    return () => el.removeEventListener('keydown', onKey);
  }, [go, total]);

  // Nothing published: drop the section rather than render an empty frame.
  // Same rule as TestimonialsSection.
  if (!item) return null;

  const cell = '1px solid var(--m-rule)';

  return (
    <section
      data-ground
      id="work"
      className="section-rhythm m-ground"
      style={{ overflow: 'hidden' }}
    >
      <div className="m-container">
        {/* ── Heading ─────────────────────────────────────────────────────── */}
        <div className="m-section-head work-head">
          <div>
            <p className="m-eyebrow mb-4">
              <span aria-hidden="true">——&nbsp;&nbsp;</span>
              WRK — {String(index + 1).padStart(2, '0')} OF {String(total).padStart(2, '0')}
            </p>
            <h2 className={SECTION_HEADING_CLASS} style={SECTION_HEADING_STYLE}>
              Selected work
            </h2>
            <p className="m-measure mt-4" style={{ color: 'var(--m-ink-6)', fontSize: 16 }}>
              A curated collection of things we have designed, built and shipped.
            </p>
          </div>
          {/* Sitting on the heading's baseline rather than under the band, so
              it stays in view for the whole section the way the reference's
              "Start a Project" cell does. */}
          <Link href="/book-a-consultation" className="m-btn m-btn--secondary work-cta">
            Start a project
            <ArrowUpRight className="w-3.5 h-3.5 m-arrow" aria-hidden="true" />
          </Link>
        </div>

        {/* ── Band ────────────────────────────────────────────────────────── */}
        <div
          ref={frameRef}
          tabIndex={0}
          role="group"
          aria-roledescription="carousel"
          aria-label="Selected work"
          className="work-band"
          style={{ borderTop: cell, borderBottom: cell }}
        >
          {/* Sphere cell */}
          <div className="work-cell work-sphere" style={{ borderRight: cell }}>
            <ParticleSphere />
          </div>

          {/* Screens cell — desktop shot above a strip of supporting shots */}
          <div className="work-cell work-screens" style={{ borderRight: cell }}>
            <div
              className="work-shot"
              style={{
                transform: `translateX(${offset}px)`,
                transition: 'transform 320ms var(--ease), opacity 320ms var(--ease)',
                opacity: offset === 0 ? 1 : 0.4,
              }}
            >
              {active?.kind === 'video' ? (
                <video
                  key={active.src}
                  src={active.src}
                  poster={active.poster}
                  autoPlay
                  muted
                  loop
                  playsInline
                  /* preload="metadata", not "auto": the poster is already
                     carrying the first impression, and a marketing page should
                     not spend a visitor's bandwidth on a loop they may scroll
                     straight past. */
                  preload="metadata"
                  aria-label={`${item.name} — walkthrough`}
                  style={{ width: '100%', height: 'auto', display: 'block' }}
                />
              ) : active ? (
                <Image
                  key={active.src}
                  src={active.src}
                  alt={`${item.name} — ${mediaIndex === 0 ? 'desktop view' : `view ${mediaIndex + 1}`}`}
                  width={1440}
                  height={900}
                  sizes="(max-width: 1023px) 100vw, 45vw"
                  style={{ width: '100%', height: 'auto', display: 'block' }}
                />
              ) : null}
            </div>
            <div
              className="work-thumbs work-thumbs-wrap"
              style={{ borderTop: cell }}
              role="tablist"
              aria-label="Views of this project"
            >
              {media.map((m, i) => (
                <button
                  key={`${m.src}-${i}`}
                  type="button"
                  role="tab"
                  aria-selected={i === mediaIndex}
                  aria-label={i === 0 ? 'Opening view' : `View ${i + 1}`}
                  onClick={() => setMediaIndex(i)}
                  className="work-thumb"
                  data-selected={i === mediaIndex}
                  style={{ borderRight: i === media.length - 1 ? 'none' : cell }}
                >
                  <Image
                    src={m.kind === 'video' ? (m.poster ?? '') : m.src}
                    alt=""
                    aria-hidden="true"
                    width={1440}
                    height={900}
                    sizes="12vw"
                    style={{ width: '100%', height: 'auto', display: 'block' }}
                  />
                  {m.kind === 'video' ? (
                    <span className="work-thumb__play" aria-hidden="true">
                      <Play className="w-3 h-3" />
                    </span>
                  ) : null}
                </button>
              ))}
            </div>
          </div>

          {/* Portrait cell */}
          <div className="work-cell work-portrait" style={{ borderRight: cell }}>
            {/* With more than one portrait shot the cell becomes a control:
                there is no room beside it for a second strip, and a caption
                that only appears when it is actually operable is honest about
                which cells respond to a click. */}
            {item.mobileImages.length > 1 && !item.mobileVideo ? (
              <button
                type="button"
                className="work-portrait__next"
                onClick={() => setMobileIndex((i) => i + 1)}
                aria-label="Next mobile view"
              >
                {(mobileIndex % item.mobileImages.length) + 1} / {item.mobileImages.length}
              </button>
            ) : null}
            <div
              style={{
                transform: `translateX(${offset}px)`,
                transition: 'transform 320ms var(--ease), opacity 320ms var(--ease)',
                opacity: offset === 0 ? 1 : 0.4,
              }}
            >
              {item.mobileVideo ? (
                <video
                  src={item.mobileVideo}
                  poster={item.mobileImages[0]}
                  autoPlay
                  muted
                  loop
                  playsInline
                  preload="metadata"
                  aria-label={`${item.name} — mobile walkthrough`}
                  style={{ width: '100%', height: 'auto', display: 'block' }}
                />
              ) : item.mobileImages.length ? (
                <Image
                  key={item.mobileImages[mobileIndex % item.mobileImages.length]}
                  src={item.mobileImages[mobileIndex % item.mobileImages.length]}
                  alt={`${item.name} — mobile view`}
                  width={390}
                  height={844}
                  sizes="(max-width: 1023px) 40vw, 16vw"
                  style={{ width: '100%', height: 'auto', display: 'block' }}
                />
              ) : null}
            </div>
          </div>

          {/* Description cell.

              It used to end with a row of tag chips — the frameworks the thing
              was built with — and that list was the tallest content in the
              band, so the height of the whole row, and the empty space in the
              three cells beside it, was set by how many framework names one
              project happened to list. The tags are still recorded and still
              editable in the dashboard; they are just not what this section is
              for. Nor was the transform readout under the sphere, which said
              `translateX(0px)` at rest and read as something left switched
              on. */}
          <div className="work-cell work-copy">
            <p className="m-eyebrow" style={{ marginBottom: 16 }}>
              {item.kind}
            </p>
            {item.description.map((para) => (
              <p
                key={para.slice(0, 24)}
                style={{
                  color: 'var(--m-ink-6)',
                  fontSize: 15,
                  lineHeight: 1.65,
                  marginBottom: 14,
                }}
              >
                {para}
              </p>
            ))}
          </div>
        </div>

        {/* ── Control bar ─────────────────────────────────────────────────── */}
        <div className="work-bar" style={{ borderBottom: cell }}>
          <button
            type="button"
            onClick={() => go(-1)}
            disabled={total < 2}
            aria-label="Previous project"
            className="work-btn"
            style={{ borderRight: cell }}
          >
            <ChevronLeft className="w-4 h-4" aria-hidden="true" />
          </button>
          <button
            type="button"
            onClick={() => go(1)}
            disabled={total < 2}
            aria-label="Next project"
            className="work-btn"
            style={{ borderRight: cell }}
          >
            <ChevronRight className="w-4 h-4" aria-hidden="true" />
          </button>
          {item.href ? (
            <Link
              href={item.href}
              aria-label={`Open ${item.name}`}
              /* Inline, not `.work-btn`: styled-jsx scopes its rules to native
                 elements it can see in this JSX, and Link is a component, so
                 the class name passed through but the scoped rule never
                 matched it. The icon rendered unsized and clipped. */
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 64,
                flex: '0 0 auto',
                color: 'var(--m-ink-5)',
                borderRight: cell,
              }}
            >
              <ArrowUpRight className="w-4 h-4 m-arrow" aria-hidden="true" />
            </Link>
          ) : null}

          {/* The project name, lower-case against the uppercase heading above
              it. The case difference carries the hierarchy so neither has to
              change size to outrank the other. */}
          <p className="work-name" aria-live="polite">
            {item.name}
          </p>
        </div>
      </div>

      <style jsx>{`
        .work-head {
          margin-bottom: 32px;
          display: flex;
          flex-direction: column;
          align-items: flex-start;
          gap: 24px;
        }
        @media (min-width: 768px) {
          .work-head {
            flex-direction: row;
            align-items: flex-end;
            justify-content: space-between;
            gap: 32px;
          }
        }
        .work-cta {
          flex: 0 0 auto;
        }
        @media (min-width: 1024px) {
          .work-head {
            margin-bottom: 40px;
          }
        }
        .work-band {
          display: grid;
          grid-template-columns: 1fr;
        }
        .work-band:focus-visible {
          outline: 2px solid var(--m-accent);
          outline-offset: 4px;
        }
        .work-cell {
          position: relative;
          min-width: 0;
          padding: 16px;
          display: flex;
          flex-direction: column;
          justify-content: center;
        }
        .work-sphere {
          display: none;
        }
        .work-screens {
          display: flex;
          flex-direction: column;
          justify-content: center;
          gap: 0;
          padding: 0;
        }
        .work-shot {
          padding: 16px;
          flex: 1 1 auto;
          display: flex;
          align-items: center;
        }
        /* Pinned to the cell floor, so the strip lines up with the bottom of
           the portrait beside it instead of floating in the middle. */
        .work-thumbs-wrap {
          margin-top: auto;
        }
        .work-thumbs {
          display: flex;
        }
        .work-thumbs > :global(*) {
          flex: 1 1 0;
          min-width: 0;
        }
        /* A button, not a div: clicking it changes what the frame above is
           showing, so it has to be reachable by keyboard and announce itself. */
        .work-thumb {
          position: relative;
          padding: 10px;
          overflow: hidden;
          background: none;
          border: 0;
          cursor: pointer;
          display: block;
          width: 100%;
          transition:
            background-color var(--dur-fast) var(--ease),
            opacity var(--dur-fast) var(--ease);
          opacity: 0.55;
        }
        .work-thumb:hover {
          opacity: 0.85;
          background: var(--m-surface);
        }
        /* The selected shot is the one in the frame above, so it reads at full
           strength and the rest step back — the strip says where you are
           rather than just what else there is. */
        .work-thumb[data-selected='true'] {
          opacity: 1;
          background: var(--m-surface);
          box-shadow: inset 0 2px 0 0 var(--m-accent);
        }
        .work-thumb:focus-visible {
          outline: 2px solid var(--m-accent);
          outline-offset: -2px;
        }
        .work-thumb__play {
          position: absolute;
          inset: 0;
          display: flex;
          align-items: center;
          justify-content: center;
          color: var(--m-on-ink);
        }
        .work-thumb__play :global(svg) {
          background: rgba(var(--m-ink-rgb), 0.72);
          border-radius: 50%;
          padding: 5px;
          width: 22px;
          height: 22px;
        }
        .work-portrait {
          display: none;
        }
        /* Capping the phone shot is what pulls ~120px of dead space out of the
           whole band: at its natural aspect it was the tallest thing in the
           row and every other cell had to match it. */
        .work-portrait__next {
          position: absolute;
          top: 10px;
          right: 10px;
          z-index: 2;
          padding: 3px 8px;
          border: 1px solid var(--m-rule);
          background: var(--m-bg);
          color: var(--m-ink-6);
          font-family: var(--font-ibm-plex-mono), 'IBM Plex Mono', ui-monospace, monospace;
          font-size: 10px;
          letter-spacing: 0.08em;
          font-variant-numeric: tabular-nums;
          cursor: pointer;
          transition: color var(--dur-fast) var(--ease);
        }
        .work-portrait__next:hover {
          color: var(--m-ink);
          border-color: var(--m-ink);
        }
        .work-portrait :global(img) {
          max-height: 340px;
          width: auto;
          margin-inline: auto;
        }
        .work-copy {
          padding: 22px 20px;
          justify-content: flex-start;
        }
        .work-bar {
          display: flex;
          align-items: stretch;
          min-height: 64px;
        }
        .work-btn {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 64px;
          flex: 0 0 auto;
          color: var(--m-ink-5);
          background: none;
          border: 0;
          transition:
            color var(--dur-fast) var(--ease),
            background-color var(--dur-fast) var(--ease);
        }
        .work-btn:hover:not(:disabled) {
          color: var(--m-ink);
          background: var(--m-surface);
        }
        .work-btn:disabled {
          opacity: 0.3;
          cursor: default;
        }
        .work-name {
          flex: 1;
          display: flex;
          align-items: center;
          justify-content: flex-end;
          padding-inline: 20px;
          font-size: 18px;
          color: var(--m-ink);
          letter-spacing: 0.01em;
        }

        @media (min-width: 768px) {
          .work-band {
            grid-template-columns: minmax(0, 1.35fr) minmax(0, 1fr);
          }
          .work-portrait {
            display: block;
          }
          .work-copy {
            grid-column: 1 / -1;
            border-top: 1px solid var(--m-rule);
          }
        }

        /* The full four-cell band only assembles where there is room for it.
           Below this the same content stacks; nothing is hidden. */
        @media (min-width: 1024px) {
          .work-band {
            grid-template-columns:
              minmax(0, 0.8fr) minmax(0, 1.75fr)
              minmax(0, 0.72fr) minmax(0, 1fr);
          }
          .work-sphere {
            display: block;
            padding: 0;
          }
          .work-copy {
            grid-column: auto;
            border-top: 0;
            padding: 24px;
          }
          .work-portrait :global(img) {
            max-height: 380px;
          }
          .work-name {
            font-size: 20px;
            justify-content: center;
          }
        }
      `}</style>
    </section>
  );
}
